import WidgetType = ToroEnums.WidgetType;

import { ISaEntity, SaArea, SaStation } from '../../api/spatial-adjust/models/spatial-adjust-course.model';
import { Injectable } from '@angular/core';
import { IWidgetDataSettings } from '../../api/_common/interfaces/widget-data-settings.interface';
import { SaBackgroundOperationStatus } from '../../api/spatial-adjust/models/sa-background-operation.model';
import { SaBannerInfo } from '../../spatial-adjust/models/sa-banner-info.model';
import { SaPushChangesStation } from '../../api/spatial-adjust/models/sa-push-changes-station.model';
import { SaSuggestedPercentAdjust } from '../../api/spatial-adjust/models/sa-suggested-percent-adjust.model';
import { SaTableItem } from '../../spatial-adjust/components/sa-dashboard/sa-dash-table/models/sa-table-item.model';
import { SaUpdateZoneChartSelection } from '../../api/spatial-adjust/models/sa-update-zone-chart-selection.model';
import { SelectItem } from 'primeng/api';
import { SpatialAdjustCourse } from '../../api/spatial-adjust/models/sa-course.model';
import { SpatialAdjustSettings } from '../../api/site/models/spatial-adjust-settings.model';
import { Subject } from 'rxjs';
import { ToroEnums } from '../enumerations/toro.enums';
import { ToroNotification } from '../../api/notifications/models/toro-notification.model';
import { WidgetDataSettings } from '../../core/dashboard/widgets/_models/widget-data-settings.model';
import { WidgetSelectItemValue } from '../../api/widgets/models/widget-select-item-value.model';


@Injectable({
    providedIn: 'root'
})
export class BroadcastService {

    // =========================================================================================================================================================
    // Dashboard Events
    // =========================================================================================================================================================

    removeWidget = new Subject<{ widgetId: number, widgetTypeId: WidgetType }>();
    showWidgetSelector = new Subject<SelectItem[]>();
    widgetSelectionChange = new Subject<WidgetSelectItemValue[]>();
    toggleSystemOverlay = new Subject<{ show: boolean, text?: string }>();
    changeWidgetConstraint = new Subject<{id: number, maxRows: number, maxCols: number}>();
    toggleWidgetDrag = new Subject<{isLocked: boolean}>();
    showFooter = new Subject<boolean>();
    appReactivate = new Subject();  // App brought back from background, minimized, etc.
    demoUserSignIn = new Subject<boolean>();

    // =========================================================================================================================================================
    // Widgets
    // =========================================================================================================================================================

    showDataSettingsDialog = new Subject<IWidgetDataSettings>();
    widgetDataSettingsChange = new Subject<WidgetDataSettings>();

    // =========================================================================================================================================================
    // User Manager
    // =========================================================================================================================================================

    // This event is fired after DashUser Preferences have been updated in the AuthManagerService. This is the only event
    // that should be subscribed to to affect UI changes (e.g., language, units, etc.) based on user preferences changes.
    userPreferencesChange = new Subject();
    groupAndDecimalSeparatorChange = new Subject();

    // =========================================================================================================================================================
    // Lynx
    // =========================================================================================================================================================

    lynxTopologyUpdatePending = new Subject<boolean>();

    // =========================================================================================================================================================
    // Turf Guard
    // =========================================================================================================================================================

    turfGuardTopologyUpdatePending = new Subject<boolean>();

    // =========================================================================================================================================================
    // myTurf
    // =========================================================================================================================================================

    myTurfUserSignedIn = new Subject();

    // =========================================================================================================================================================
    // intelli360
    // =========================================================================================================================================================

    i3UserSignedIn = new Subject();

    // =========================================================================================================================================================
    // Notifications Service
    // =========================================================================================================================================================

    notificationsRetrieved = new Subject<ToroNotification[]>();

    // =========================================================================================================================================================
    // Setup Wizard
    // =========================================================================================================================================================

    setupDoConnect = new Subject();
    setupToggleWidgetState = new Subject<{type: WidgetType, isEnabled: boolean}>();
    setupCompleteIntegration = new Subject();
    showSetupWizard = new Subject();
    setupTaskTrackerApiKeyChanged = new Subject<string>();
    setupPlaybooksCourseIdChanged = new Subject<string>();
    setupPerryWeatherIntegrationKeyChanged = new Subject<string>();
    setupSoilScoutLoginChanged = new Subject();
    setupSpectrumApiKeyChanged = new Subject<string>();
    setupStrikeGuardIdChanged = new Subject<string>();
    setupMayaApiTokenChanged = new Subject<string>();
    setupTurfProApiTokenChanged = new Subject<string>();

    // =========================================================================================================================================================
    // Spatial Adjust
    // =========================================================================================================================================================

    spatialAdjustLoaded = new Subject<boolean>();
    spatialAdjustDataLoaded = new Subject<SaArea[]>();
    saTableItemSelected = new Subject<SaTableItem>();
    saTableItemEnabledChanged = new Subject<ISaEntity>();
    saUserPercentAdjustChanged = new Subject<ISaEntity>();
    saSuggestedPctAdjChange = new Subject<SaStation>();
    mapStationSelected = new Subject<string>();
    pushingAdjustmentsToLynx = new Subject<number>();
    pendingAdjustChangesUpdated = new Subject<SaPushChangesStation[]>();
    selectedSprinklerKeyChanged = new Subject<string>();
    pushToLynxCloudComplete = new Subject<string[]>();
    saTargetVwcChanged = new Subject<{ stationName: string, targetVwcPercent: number }[]>();
    saSettingChanged = new Subject<SpatialAdjustSettings>();
    saMinThresholdChanged = new Subject<SpatialAdjustSettings>();
    saStationFactorsChanged = new Subject();
    saSuggestedPctAdjustmentChanged = new Subject<SaSuggestedPercentAdjust[]>();
    saAlgorithmChanged = new Subject<ToroEnums.SaAlgorithm>();
    saBulkAdjustCompleted = new Subject();                                  // Used to notify Dashboard to reload primary data (picking up modified Adjustments).
    saTableItemsChange = new Subject<SaTableItem[]>();                      // Used to notify Chart when the items in the table have changed.
    saChartSelectionChange = new Subject<SaUpdateZoneChartSelection>();     // Used to notify Table when the selected stations in the Chart have changed.
    saTableSelectionChange = new Subject();                                 // Used to notify any subscribers when the selected items in the table have changed.
    saCourseListUpdated = new Subject<SpatialAdjustCourse[]>();             // Notify when the list of courses has been updated (e.g., after connecting to Lynx or refreshing courses).
    saCourseSelectionChange = new Subject<SpatialAdjustCourse>();           // For multiple courses - notify when the selected course has changed.
    saLoadingTopologyData = new Subject<boolean>();                         // Used to indicate when topology data is being loaded, so that dependent components can show loading indicators as needed.
    saShowTopBanner = new Subject<SaBannerInfo>();                          // Used to show Top Banner to alert user.
    saFooterStatus = new Subject<string>();
    saFooterStatusWithTimeout = new Subject<{ message: string, timeoutSec?: number; }>();
    saTurfRadScanDataRetrieved = new Subject();
    saReloadScanData = new Subject();
    saFailedToLoadLynxData = new Subject();
    saRequestShowThresholdSettings = new Subject();
    saTargetProfileUpsertStatus = new Subject<SaBackgroundOperationStatus>();
}

